import {
  BaseMediaRecorderClient,
  MediaRecorderClientBaseParams,
  VIDEO_BITS_PER_SECOND,
} from 'lib/clients/mediaRecorder/BaseMediaRecorderClient';
import { FileType } from 'lib/file';

type SupportedMimeType = FileType.VideoMp4 | FileType.VideoWebm;

/**
 * The amount of time MediaRecorder uses to chunk the data by
 */
const MEDIA_RECORDER_TIME_SLICE = 200;

const MAX_RECORDING_TIME = 120_000;

/**
 * 64 kbps - average quality used for low complexity sounds (e.g. radio)
 */
const AUDIO_BITS_PER_SECOND = 64_000;

/**
 * Mimetypes were determined by visiting this site on various browsers: https://www.sitelint.com/lab/media-recoder-supported-mime-type/
 * Prioritizing webm as it has better compression than mp4
 */
function getSupportedMimeType(): SupportedMimeType | null {
  if (!window.MediaRecorder) {
    return null;
  }
  const mimeTypes = [FileType.VideoWebm, FileType.VideoMp4];
  return mimeTypes.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) as SupportedMimeType;
}

export interface RecordedCapturedVideoData {
  type: 'upload';
  chunks: Blob[];
  mimeType: SupportedMimeType;
}

export interface LocalMediaRecorderClientParams extends MediaRecorderClientBaseParams {
  video?: HTMLVideoElement | null;
  timeSlice?: number;
  maxRecordingTime?: number;
  onDataAvailable?: (chunks: Blob) => void;
}

/**
 * Interface for handling an instance of a MediaRecorder. This Client is a singleton that uses an Observer model so it can
 * live across multiple routes / views
 */
class Client extends BaseMediaRecorderClient<LocalMediaRecorderClientParams> {
  private chunks: Blob[] = [];
  private mediaRecorder?: MediaRecorder | null;
  private mimeType?: SupportedMimeType | null;

  public dataAvailableCalled: boolean = false;

  public type = 'upload' as const;

  public init(params: LocalMediaRecorderClientParams): string | undefined {
    this.params = params;
    const { stream, onStart, onError, onRecordingComplete, onTimeExceeded } = params;

    if (!this.isSupported()) {
      onError?.('MediaRecorder error: MediaRecorder is not supported');
      return;
    }

    if (!stream) {
      onError?.('MediaRecorder error: stream must be provided');
      return;
    }

    this.mimeType = getSupportedMimeType();

    if (!this.mimeType) {
      onError?.('MediaRecorder error: mimetype not supported');
      return;
    }

    return this.subscribe({
      onStart,
      onError,
      onRecordingComplete,
      onTimeExceeded,
    });
  }

  public isRecording() {
    return !!this.mediaRecorder;
  }

  public isSupported() {
    return !!window.MediaRecorder;
  }

  public startRecording() {
    // Note: you need to reinitialize MediaRecorder every time you restart because otherwise the stream will be corrupt
    this.createMediaRecorder();

    // check if it exists, in case createMediaRecorder errors
    if (this.mediaRecorder && this.mediaRecorder.state !== 'recording') {
      this.mediaRecorder.start(this.params?.timeSlice ?? MEDIA_RECORDER_TIME_SLICE);
    }
  }

  public stopCapture() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
  }

  public get stream(): MediaStream | undefined {
    return this.params?.stream;
  }

  public destroy() {
    this.stopCapture();
    this.cleanUpMediaRecorder();
    this.clearSubscribers();
    this.dataAvailableCalled = false;
  }

  private cleanUpMediaRecorder() {
    if (!this.isRecording()) {
      return this;
    }

    this.chunks = [];

    if (this.mediaRecorder) {
      this.mediaRecorder.removeEventListener('start', this.onStartedRecording);
      this.mediaRecorder.removeEventListener('dataavailable', this.handleDataAvailable);
      this.mediaRecorder.removeEventListener('error', this.handleError);
      this.mediaRecorder.removeEventListener('stop', this.handleComplete);
      this.mediaRecorder = null;
    }

    this.clearTimeouts();
  }

  private createMediaRecorder() {
    try {
      if (!this.params?.stream || !this.mimeType) {
        throw new Error('LocalMediaRecorderClient: stream and mimetype must be provided');
      }

      this.mediaRecorder = new MediaRecorder(this.params.stream, {
        mimeType: this.mimeType,
        videoBitsPerSecond: this.params?.bitRate ?? VIDEO_BITS_PER_SECOND,
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
      });
      this.mediaRecorder.addEventListener('start', this.onStartedRecording);
      this.mediaRecorder.addEventListener('dataavailable', this.handleDataAvailable);
      this.mediaRecorder.addEventListener('error', this.handleError);
      this.mediaRecorder.addEventListener('stop', this.handleComplete);
    } catch (err) {
      this.notify('error', err);
    }
  }

  private handleDataAvailable = (e: BlobEvent) => {
    this.dataAvailableCalled = true;
    if (e.data?.size > 0) {
      this.chunks.push(e.data);

      if (this.getElapsedTime() > (this.params?.maxRecordingTime ?? MAX_RECORDING_TIME)) {
        this.notify('time-exceeded');
      }
    }
  };

  private handleComplete = () => {
    this.onStoppedRecording({
      chunks: this.chunks,
      // At this point mimetype should always be defined because it's required to initialize the MediaRecorder
      mimeType: this.mimeType as SupportedMimeType,
      type: this.type,
    });
  };

  // Note: there is only partial browser support for extracting the details of this event
  // so it might not actually be an Error or an Event at all.
  // see https://developer.mozilla.org/en-US/docs/Web/API/MediaRecorder/error_event
  private handleError = (e: Event | string | unknown) => {
    let message;
    if (e instanceof Event) {
      // If this is a MediaRecorderErrorEvent, it will have an error object.
      // Coerce to `any` because MediaRecorderErrorEvent is non-standard
      // https://developer.mozilla.org/en-US/docs/Web/API/MediaRecorderErrorEvent/error
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      message = `Event: ${(e as any).error?.name}`;
    } else {
      message = JSON.stringify(e);
    }
    this.notify('error', message);
  };
}

export const LocalMediaRecorderClient = new Client();
